import React from "react";
import { useQuery } from "@tanstack/react-query";
import blogService from "../../../services/blogService";
import GuideCard from "../../../pages/blog/GuideCard";
import SectionHeader from "./SectionHeader";

/**
 * Latest Guides: homepage teaser for the blog.
 *
 * Shows the 3 most recent posts using the canonical GuideCard (shared with
 * /blog), with a "View all" link to /blog (via SectionHeader). This is a low-stakes,
 * bottom-of-page content block: it must NEVER show a loading skeleton or an
 * error state on the homepage. It renders nothing until we have at least one
 * post, and stays hidden on empty result or fetch failure.
 */
const LatestGuidesSection = ({ navigate }) => {
  const { data: posts = [] } = useQuery({
    queryKey: ["blog", "latest"],
    queryFn: () => blogService.listPosts(),
    staleTime: 5 * 60 * 1000, // 5 min (guides change rarely)
    // Silent failure: no retries spamming, and the empty default hides the block.
    retry: 1,
    // A homepage teaser is non-critical; don't refetch aggressively.
    refetchOnWindowFocus: false,
  });

  const latest = Array.isArray(posts) ? posts.slice(0, 3) : [];

  // Graceful hide: nothing to show (empty, still loading, or fetch failed).
  if (latest.length === 0) return null;

  return (
    <section className="mb-12">
      <SectionHeader
        title="Latest Guides"
        kicker="Learn & Win"
        onViewAll={() => navigate("/blog")}
      />
      <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3 md:gap-7">
        {latest.map((post) => (
          <GuideCard key={post.slug} post={post} />
        ))}
      </div>
    </section>
  );
};

export default LatestGuidesSection;
